/**
 * FERP Portal - Gantt Data Hook
 *
 * Fetches tasks (and optionally dependencies) for the Gantt chart.
 * Uses the same useListTasksQuery as the board view.
 *
 * When a workflowId is provided, also fetches dependencies for that workflow
 * so the Gantt chart can draw dependency arrows.
 */

import { useMemo } from 'react';
import {
  useListTasksQuery,
  useListDependenciesByWorkflowQuery,
  type TaskFieldsFragment,
  type DependencyFieldsFragment,
} from '@/generated/graphql';
import { useTenantId } from '@/auth';

export interface UseGanttDataOptions {
  /** If provided, fetches dependencies for this workflow */
  workflowId?: string;
  /** Override tasks with demo data */
  demoTasks?: TaskFieldsFragment[];
  /** Override dependencies with demo data */
  demoDependencies?: DependencyFieldsFragment[];
}

export interface UseGanttDataReturn {
  tasks: TaskFieldsFragment[];
  dependencies: DependencyFieldsFragment[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useGanttData(options: UseGanttDataOptions = {}): UseGanttDataReturn {
  const { workflowId, demoTasks, demoDependencies } = options;
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const isLive = !demoTasks && !skip;

  const {
    data: taskData,
    loading: taskLoading,
    error: taskError,
    refetch: refetchTasks,
  } = useListTasksQuery({
    variables: { limit: 200 },
    skip: !isLive,
    pollInterval: 30_000,
  });

  const {
    data: depData,
    loading: depLoading,
    error: depError,
    refetch: refetchDeps,
  } = useListDependenciesByWorkflowQuery({
    variables: { workflowId: workflowId ?? '', limit: 200 },
    skip: !isLive || !workflowId,
  });

  const tasks = useMemo(() => {
    if (demoTasks) return demoTasks;
    return taskData?.taskApiListTasks?.items ?? [];
  }, [demoTasks, taskData]);

  const dependencies = useMemo(() => {
    if (demoDependencies) return demoDependencies;
    return depData?.listDependenciesByWorkflow?.items ?? [];
  }, [demoDependencies, depData]);

  const refetch = () => {
    if (isLive) {
      refetchTasks();
      if (workflowId) refetchDeps();
    }
  };

  return {
    tasks,
    dependencies,
    loading: demoTasks ? false : (taskLoading || depLoading),
    error: demoTasks ? null : (taskError ?? depError ?? null),
    refetch,
  };
}
